import imageOptimizationAPI from '../api/imageOptimization.js';
import { uploadToSiteMedia } from './supabaseMediaUpload.js';

/**
 * File Upload Service
 * Handles file uploads with automatic image optimization
 * Production: Supabase Storage (Hostinger has no Node /api)
 * DEV: try local /api/optimize-image first, fall back to Storage
 */
class FileUploadService {
  constructor() {
    this.maxFileSize = 10 * 1024 * 1024; // 10MB
    this.allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  }

  /**
   * Validate uploaded file
   */
  validateFile(file) {
    const errors = [];

    if (!file) {
      errors.push('No file selected');
      return { valid: false, errors };
    }

    if (file.size > this.maxFileSize) {
      errors.push(`File size must be less than ${this.maxFileSize / (1024 * 1024)}MB`);
    }

    if (!this.allowedTypes.includes(file.type)) {
      errors.push(`File type must be one of: ${this.allowedTypes.join(', ')}`);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Convert file to base64 for preview
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Upload and optimize image
   */
  async uploadAndOptimizeImage(file, vehicleName, vehicleType) {
    try {
      const validation = this.validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      console.log(`Uploading and optimizing: ${file.name}`);

      // DEV only: try local Node optimize API when available
      if (import.meta.env.DEV) {
        try {
          const apiResult = await imageOptimizationAPI.uploadAndOptimizeImage(
            file,
            vehicleName,
            vehicleType
          );
          if (apiResult?.success && apiResult.optimizedPath) {
            console.log('Local optimize API succeeded');
            return apiResult;
          }
          console.warn('Local optimize API failed, using Supabase Storage:', apiResult?.error);
        } catch (apiError) {
          console.warn('Local optimize API unavailable, using Supabase Storage:', apiError.message);
        }
      }

      // Production (and DEV fallback): Supabase Storage
      const storageResult = await uploadToSiteMedia(file, vehicleName, vehicleType);
      console.log('Supabase Storage upload complete:', storageResult.optimizedPath);
      return storageResult;
    } catch (error) {
      console.error('Upload and optimization failed:', error.message);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Generate filename based on vehicle info
   */
  generateFilename(vehicleName, vehicleType, originalFileName) {
    const name = vehicleName.toLowerCase().replace(/\s+/g, '-');
    const type = vehicleType.toLowerCase();
    const extension = originalFileName.split('.').pop() || 'jpg';

    return {
      baseName: name,
      typeDir: type,
      extension: extension
    };
  }

  /**
   * Get file size in human readable format
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file type icon
   */
  getFileTypeIcon(fileType) {
    if (fileType.startsWith('image/')) {
      return 'image';
    }
    return 'file';
  }
}

const fileUploadService = new FileUploadService();

export default fileUploadService;
